/**
 * What the engine needs to know about a printed card, and where it gets it.
 *
 * `packages/engine` has **zero runtime dependencies** (SPEC.md §2) — it never imports
 * `@lorcana/card-data`; that package depends on this one. So the engine declares the
 * structural subset of `CardDef` it reads, and a game is created with a *catalog* that
 * looks cards up by LorcanaJSON id. `CARDS_BY_ID` from `@lorcana/card-data` satisfies
 * `CardCatalog` as-is (a `ReadonlyMap<number, CardDef>` is structurally a catalog).
 */

import type { Ability, AbilityRegistry } from './abilities/ir.ts';
import { isKeywordOnlyAndComplete } from './abilities/keywords.ts';

export type CardTypeName = 'Action' | 'Character' | 'Item' | 'Location';

/**
 * The printed characteristics the engine reads. Deliberately a *structural subset* of
 * `@lorcana/card-data`'s `CardDef`, so a `CardDef` is assignable to it without conversion.
 * Optional fields are absent (not `undefined`) exactly as they are on `CardDef`.
 */
export interface CardDefinition {
  readonly id: number;
  readonly name: string;
  readonly fullName: string;
  readonly type: CardTypeName;
  readonly cost: number;
  readonly inkwell: boolean;
  readonly inks: readonly string[];
  readonly names: readonly string[];
  readonly subtypes: readonly string[];
  readonly keywords: readonly string[];
  /**
   * LorcanaJSON's structured abilities. The engine reads only the keyword rows — `keyword`
   * names the keyword and `keywordValueNumber` its parameter (Challenger +2, Shift 5) — which
   * is what makes keyword-only cards free of hand-authoring (SPEC.md §5.3).
   */
  readonly abilities: readonly {
    readonly type: string;
    readonly name?: string;
    readonly keyword?: string;
    readonly keywordValueNumber?: number;
  }[];
  readonly fullText: string;
  readonly strength?: number;
  readonly willpower?: number;
  readonly lore?: number;
  readonly moveCost?: number;
}

/**
 * How a game looks a printed card up. `ReadonlyMap<number, CardDef>` is one.
 *
 * `abilities` is the optional second half: the **hand-authored ability IR** for a card, which
 * `packages/cards` supplies and `packages/card-data` cannot (behaviour needs judgement;
 * statistics are generated). Keeping it on the catalog rather than on `GameState` means the
 * state still has exactly one non-plain-data field, and `createGame` still takes one object.
 * `makeCatalog(cards, registry)` builds one.
 */
export interface CardCatalog {
  get(defId: number): CardDefinition | undefined;
  abilities?(defId: number): readonly Ability[] | undefined;
  /**
   * Every card name a player may **declare** for a "name a card" decision (SPEC.md §5.2's
   * escape hatch raises one): base names plus each name of a multi-name card, deduplicated
   * and sorted. Optional — `declarableNames` falls back to enumerating a `Map`-shaped
   * catalog, and a catalog that can do neither simply cannot serve a naming decision.
   */
  names?(): readonly string[];
}

/**
 * The names one card answers to: its base name plus LorcanaJSON's `names` list. For most
 * cards the two are the same one name; a multi-name card ("Flotsam & Jetsam") is named its
 * joint title **and** each half, which is both what a player may declare and what "the same
 * name as" matches (CR §5.3.2 — a card with two names counts as having both).
 */
export function cardNamesOf(def: CardDefinition): readonly string[] {
  return def.names.includes(def.name) ? def.names : [def.name, ...def.names];
}

/** Computed rosters, one per catalog object — a catalog is stable for a whole game. */
const DECLARABLE_CACHE = new WeakMap<object, readonly string[]>();

/**
 * The full "name a card" roster a catalog can offer, or `null` when it cannot enumerate its
 * cards at all. Prefers the catalog's own `names()`; a bare `ReadonlyMap<number, CardDef>` —
 * which is a `CardCatalog` structurally — is enumerated through its `values()`. Sorted by
 * code point and deduplicated, so the option list is canonical (SPEC.md §4.4).
 */
export function declarableNames(catalog: CardCatalog): readonly string[] | null {
  if (catalog.names !== undefined) return catalog.names();
  const cached = DECLARABLE_CACHE.get(catalog);
  if (cached !== undefined) return cached;
  const values = (catalog as { values?: () => Iterable<CardDefinition> }).values;
  if (typeof values !== 'function') return null;
  const found = new Set<string>();
  for (const def of values.call(catalog)) for (const name of cardNamesOf(def)) found.add(name);
  const roster = [...found].sort();
  DECLARABLE_CACHE.set(catalog, roster);
  return roster;
}

/** Join a generated card catalog with a hand-authored ability registry. */
export function makeCatalog(cards: CardCatalog, registry: AbilityRegistry): CardCatalog {
  const canEnumerate =
    cards.names !== undefined ||
    typeof (cards as { values?: unknown }).values === 'function';
  return {
    get: (defId) => cards.get(defId),
    abilities: (defId) => registry.get(defId),
    // The joined catalog hides the underlying Map, so the roster capability is carried over
    // here — `declarableNames` of the result would otherwise see neither `names` nor `values`.
    ...(canEnumerate ? { names: () => declarableNames(cards) ?? [] } : {}),
  };
}

/** The definition for this id; throws if the catalog doesn't have it. */
export function requireDefinition(catalog: CardCatalog, defId: number): CardDefinition {
  const def = catalog.get(defId);
  if (def === undefined) throw new Error(`Card catalog has no card with id ${defId}`);
  return def;
}

/**
 * A card with no abilities, no keywords and no rules text at all — the "vanilla" pool that
 * generates for free (SPEC.md §5.3). ~310 of the 3,242 released cards qualify.
 *
 * **Characters and locations both.** A location with no rules text is exactly as vanilla as a
 * character with none: everything it does — paying out its ◊ in the Set step, holding
 * characters, being challenged — is the engine's own turn structure (CR §3.2.2, §4.3.6, §5.6),
 * printed on no card and needing no module. An Item or an Action with no text is not the same
 * thing: those are cards that *do* nothing, and none is released.
 *
 * **Ink color is irrelevant.** Every Set 1–13 card prints at least one ink, but the
 * Illumineer's Quest "Entangled" promos (`inks: []`) are colorless by design — that has
 * nothing to do with whether a card carries rules text (ticket 131). Constructed-legality
 * ink filtering is a separate concern the deck builders (`vanillaCardPool` and friends) own.
 */
export function isVanillaCharacter(def: CardDefinition): boolean {
  return (
    (def.type === 'Character' || def.type === 'Location') &&
    def.abilities.length === 0 &&
    def.keywords.length === 0 &&
    def.fullText === ''
  );
}

/**
 * Whether the engine can currently play this card at all. Unimplemented cards hard-block
 * game start (SPEC.md §5.3) — `createGame` refuses a deck containing any.
 *
 * Three ways a card qualifies:
 *
 * 1. it prints no rules text at all (vanilla);
 * 2. its whole rules text is keywords the engine plays **completely** — the 231-card
 *    zero-authoring share. A card carrying a keyword that waits on a later ticket (Shift,
 *    Boost, Singer, Sing Together) does **not** qualify: playing it with the keyword silently
 *    ignored would be a wrong game, and partial implementation is forbidden;
 * 3. the catalog serves hand-authored ability IR for it (`packages/cards`).
 *
 * Ticket 14 replaces (3) with the generated coverage manifest.
 */
export function isSupportedCard(def: CardDefinition, catalog?: CardCatalog): boolean {
  if (isVanillaCharacter(def)) return true;
  if (catalog?.abilities?.(def.id) !== undefined) return true;
  return isKeywordOnlyAndComplete(def);
}
